<script setup lang="ts">
import { useCounter, useNetwork, useTimeoutFn } from '@vueuse/core';
import { APP } from '../../configs/app';
import Modal from './Modal.vue';
import { computed, reactive, ref } from 'vue';
import { useClassStore, useLangStore, useQuizStore, useSessionStore, useUserStore } from '../../stores';
import Button from './Button.vue';
import { Session, VocabResult } from '../../types/session';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue'
const appVersionDisplay = APP.env === 'product' ? `v${APP.version}` : `v${APP.version}-${APP.env}`

const network = reactive(useNetwork())

const clickTimeout = 500
const clickRequited = 5
const { count, inc, reset } = useCounter(0);
import { QuizState } from '../../types/quiz';

const modalShow = ref<boolean>(false);

const session = useSessionStore()
const classStore = useClassStore()
const user = useUserStore()
const quizStore = useQuizStore()
const vocabRecordAttempts = computed<VocabResult[]>(() => session.vocabRecordAttempts?.[classStore.roomName]?.[user.username || '']?.[session.currentVocabId]?.[session.vocabRecordingType] || [])

const { start } = useTimeoutFn(() => reset(), clickTimeout, { immediate: false });

const handleClick = () => {
    inc()
    if (count.value < clickRequited) {
        // Restart timeout on each click
        start()
        return
    }

    modalShow.value = true
    reset()
}

const handleReload = () => {
    window.location.reload()
}

const lang = useLangStore()
function showTranslationKeys() {
    lang.i18n.global.setLocaleMessage(lang.locale, {})
}

</script>

<template>
    <Teleport to="body">
        <div class="text-neutral-400 fixed z-50 bottom-0 left-1 text-sm opacity-50 select-none" @click="handleClick">
            {{ appVersionDisplay }}
        </div>
    </Teleport>

    <Modal v-model="modalShow" size="medium" :full-height="true" :static="true">
        <div class="flex flex-col w-full h-full overflow-hidden gap-3">
            <TabGroup as="div" class="flex gap-2 flex-col w-full flex-1 overflow-hidden">
                <TabList aria-selected="div" class="flex space-x-1 rounded-xl bg-gradient-to-r from-sky-400 to-blue-600 p-1">
                    <Tab as="template" v-slot="{ selected }">
                        <button class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 " :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                            Network data
                        </button>
                    </Tab>
                    <Tab as="template" v-slot="{ selected }">
                        <button class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 " :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                            Class content
                        </button>
                    </Tab>
                    <Tab as="template" v-slot="{ selected }">
                        <button class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 " :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                            Vocab data
                        </button>
                    </Tab>
                    <Tab as="template" v-slot="{ selected }">
                        <button class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 " :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                            Practice data
                        </button>
                    </Tab>
                    <Tab as="template" v-slot="{ selected }">
                        <button class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 " :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                            Quiz data
                        </button>
                    </Tab>

                </TabList>
                <TabPanels as="div" class="w-full flex-1 bg-neutral-100 p-5 rounded-xl  overflow-auto relative">
                    <TabPanel>
                        <Button class="w-full mb-3" @click="showTranslationKeys">
                            Show translation keys
                        </Button>
                        <pre>{{ JSON.stringify(network, null, 2) }}</pre>
                    </TabPanel>
                    <TabPanel>
                        <pre>{{ JSON.stringify(session.classContent, null, 2) }}</pre>
                    </TabPanel>
                    <TabPanel>
                        <pre>{{ JSON.stringify(vocabRecordAttempts, null, 2) }}</pre>
                    </TabPanel>
                    <TabPanel>
                        <pre>{{ session.practiceUrl }}</pre>
                        <pre>{{ JSON.stringify(session.currentPracticeResult, null, 2) }}</pre>
                    </TabPanel>
                    <TabPanel>
                        <Button class="w-full mb-3" @click="quizStore.updateQuizState(QuizState.NOT_STARTED)">
                            Reset quiz
                        </Button>
                        <pre>{{ JSON.stringify(session.classContent?.[Session.quiz], null, 2) }}</pre>
                    </TabPanel>
                </TabPanels>
            </TabGroup>
            <div class="flex w-full justify-center gap-5">
                <Button @click="handleReload">Reload</Button>
            </div>
        </div>
    </Modal>
</template>